import { useEffect, useRef, useState, useCallback } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';

const API_BASE = process.env.REACT_APP_API_URL ||
  (typeof window !== 'undefined' ? `http://${window.location.hostname}:8080/api` : '/api');

export function useWebSocket(onSpaceEvent) {
  const clientRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [liveAlerts, setLiveAlerts] = useState([]);

  const addAlert = useCallback((alert) => {
    setLiveAlerts(prev => [{ ...alert, id: Date.now() }, ...prev].slice(0, 10));
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');

    if (!token) {
      // Guest: just ping the health endpoint to show Live/Offline
      const check = async () => {
        try {
          const res = await fetch(`${API_BASE}/actuator/health`, { method: 'GET' });
          setConnected(res.ok);
        } catch {
          setConnected(false);
        }
      };
      check();
      const interval = setInterval(check, 30000);
      return () => clearInterval(interval);
    }

    const wsUrl = process.env.REACT_APP_WS_URL || `${API_BASE}/ws`;

    const client = new Client({
      webSocketFactory: () => new SockJS(wsUrl),
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: 5000,
      onConnect: () => {
        setConnected(true);
        client.subscribe('/topic/space-events', (msg) => {
          const event = JSON.parse(msg.body);
          addAlert(event);
          onSpaceEvent?.(event);
        });
        client.subscribe('/topic/data-refresh', () => {
          onSpaceEvent?.({ type: 'DATA_REFRESH' });
        });
      },
      onDisconnect: () => setConnected(false),
      onStompError: () => setConnected(false),
    });

    client.activate();
    clientRef.current = client;

    return () => { client.deactivate(); };
  }, []); // eslint-disable-line

  const dismissAlert = useCallback((id) => {
    setLiveAlerts(prev => prev.filter(a => a.id !== id));
  }, []);

  return { connected, liveAlerts, dismissAlert };
}
